import { useCallback, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useReactToPrint } from "react-to-print";

import { Button, Buttons, Color, LoadingGroup, LoadingSkeleton, Message, Messages, Tag, TagType } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subtitle, TextColor } from "capitalrx-components-2.0/lib/typography";
import { CaseTags } from "capitalrx-types";
import { formatDate } from "capitalrx-utilities";
import { ReactNode } from "react";

import { useGetPriorAuthorizationCaseQuery, useListPriorAuthorizationDecisionLetter } from "@/App/api/priorAuthorization";
import { PrintableWrapper } from "@/App/components";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { formatPhoneNumber, getCustomerSupportNumber } from "@/App/utils/utils";

const TRANSLATION_PATH = "PRINT_PRIOR_AUTHORIZATION_DETAILS_PAGE";

const InlineField = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <Body>
      <Semibold>{label}:</Semibold>{" "}{children}
    </Body>
  );
};

const SectionHeader = ({ title }: { title: string }) => {
  return (
    <Body><Semibold><TextColor appearance={Color.DarkGrey["400"]}><span style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</span></TextColor></Semibold></Body>
  );
};

export const PrintPriorAuthorizationDetailsPage = () => {
  const { t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { priorAuthorizationPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = priorAuthorizationPhoneNumber ? priorAuthorizationPhoneNumber : getCustomerSupportNumber(memberEligibility);
  const [searchParams, _] = useSearchParams();
  const pa_case_id = searchParams.get("pa_case_id");
  const printRef = useRef();
  const { data: priorAuthorizationCaseData, isLoading: priorAuthorizationCaseIsLoading } = useGetPriorAuthorizationCaseQuery({
    id: +pa_case_id,
  });
  const { data: documentListData, isLoading, isError } = useListPriorAuthorizationDecisionLetter({ case_id: +pa_case_id });
  const caseOutcomesObject = {
    approved: [translate(`${TRANSLATION_PATH}.approved`), "success-high-contrast"],
    denied: [translate(`${TRANSLATION_PATH}.denied`), "error-high-contrast"],
    closed: [translate(`${TRANSLATION_PATH}.closed`), "info-high-contrast"],
    in_progress: [translate(`${TRANSLATION_PATH}.inProgress`), "warning-high-contrast"],
  };
  const caseOutcome = priorAuthorizationCaseData?.case_outcome?.outcome || "in_progress";

  const dateFormat = "MM/DD/YYYY";

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrint = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.PRIOR_AUTHORIZATION.printPage);
    handlePrint();
  }, [handlePrint]);

  const prescriberLocation = priorAuthorizationCaseData?.prescriber_practice_location;

  const prescriberDisplayName = useMemo(() => {
    const firstName = priorAuthorizationCaseData?.prescriber_first_name ?? "";
    const lastName = priorAuthorizationCaseData?.prescriber_last_name ?? "";
    const name = `${firstName} ${lastName}`.trim();
    const credential = prescriberLocation?.credential;
    return credential ? `${name}, ${credential}` : name;
  }, [priorAuthorizationCaseData?.prescriber_first_name, priorAuthorizationCaseData?.prescriber_last_name, prescriberLocation?.credential]);

  const formattedPrescriberPhoneNumber = useMemo(() => {
    return formatPhoneNumber(priorAuthorizationCaseData?.prescriber_business_practice_phone);
  }, [priorAuthorizationCaseData?.prescriber_business_practice_phone]);

  const patientName = `${priorAuthorizationCaseData?.member_first_name ?? ""} ${priorAuthorizationCaseData?.member_last_name ?? ""}`.trim();

  const drugNameWithDetails = useMemo(() => {
    const parts = [priorAuthorizationCaseData?.drug_name];
    if (priorAuthorizationCaseData?.drug_dosage) parts.push(priorAuthorizationCaseData.drug_dosage);
    if (priorAuthorizationCaseData?.drug_dosage_form) parts.push(priorAuthorizationCaseData.drug_dosage_form);
    return parts.filter(Boolean).join(" ");
  }, [priorAuthorizationCaseData?.drug_name, priorAuthorizationCaseData?.drug_dosage, priorAuthorizationCaseData?.drug_dosage_form]);

  const renderCaseFields = () => {
    const fields: ReactNode[] = [];

    fields.push(
      <InlineField key="patient" label={translate(`${TRANSLATION_PATH}.patient`)}>{patientName}</InlineField>,
    );
    fields.push(
      <InlineField key="id" label={translate(`${TRANSLATION_PATH}.id`)}>{priorAuthorizationCaseData?.case_id}</InlineField>,
    );

    if (caseOutcome === "in_progress") {
      fields.push(
        <InlineField key="requested" label={translate(`${TRANSLATION_PATH}.requested`)}>{formatDate(priorAuthorizationCaseData?.received_at, dateFormat, true)}</InlineField>,
      );
    }

    if (caseOutcome === "approved") {
      fields.push(
        <InlineField key="requested" label={translate(`${TRANSLATION_PATH}.requested`)}>{formatDate(priorAuthorizationCaseData?.received_at, dateFormat, true)}</InlineField>,
      );
      fields.push(
        <InlineField key="approved" label={translate(`${TRANSLATION_PATH}.approved`)}>{formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}</InlineField>,
      );
      fields.push(
        <InlineField key="coverageStarts" label={translate(`${TRANSLATION_PATH}.coverageStarts`)}>{formatDate(priorAuthorizationCaseData?.prior_authorization?.effective_date, dateFormat)}</InlineField>,
      );
      fields.push(
        <InlineField key="coverageExpires" label={translate(`${TRANSLATION_PATH}.coverageExpires`)}>{formatDate(priorAuthorizationCaseData?.prior_authorization?.expiration_date, dateFormat)}</InlineField>,
      );
      if (priorAuthorizationCaseData?.is_renewal_available && priorAuthorizationCaseData?.renewal_start_date) {
        fields.push(
          <InlineField key="renewalAvailable" label={translate(`${TRANSLATION_PATH}.renewalAvailable`)}>{formatDate(priorAuthorizationCaseData?.renewal_start_date, dateFormat)}</InlineField>,
        );
      }
    }

    if (caseOutcome === "denied") {
      fields.push(
        <InlineField key="denied" label={translate(`${TRANSLATION_PATH}.denied`)}>{formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}</InlineField>,
      );
      if (priorAuthorizationCaseData?.case_outcome?.denial_type != null) {
        fields.push(
          <InlineField key="denialType" label={translate(`${TRANSLATION_PATH}.denialType`)}>{translate(`${TRANSLATION_PATH}.denialTypes.${priorAuthorizationCaseData?.case_outcome?.denial_type}`)}</InlineField>,
        );
      }
      if (priorAuthorizationCaseData?.case_outcome?.denial_reason != null) {
        fields.push(
          <InlineField key="denialReason" label={translate(`${TRANSLATION_PATH}.denialReason`)}>{translate(`${TRANSLATION_PATH}.denialReasons.${priorAuthorizationCaseData?.case_outcome?.denial_reason}`)}</InlineField>,
        );
      }
    }

    if (caseOutcome === "closed") {
      fields.push(
        <InlineField key="closed" label={translate(`${TRANSLATION_PATH}.closed`)}>{formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}</InlineField>,
      );
    }

    return <Stack spacing="none">{fields}</Stack>;
  };

  return (
    <LoadingGroup loading={priorAuthorizationCaseIsLoading}>
      <PrintableWrapper ref={printRef} innerPadding="2.5rem">
        <LoadingGroup.Placeholder fallback={<PADetailsSkeleton />}>
          <Shelf alignment="span">
            <Shelf.Spacer />
            <Button appearance={Buttons.Functional.Tertiary.Large} icon="print" onClick={onPrint} />
          </Shelf>
          <Stack spacing="large">
            {/* Header */}
            <Stack spacing="mini" alignment="left">
              <Body><Semibold><TextColor appearance={Color.DarkGrey["400"]}><span style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{translate(`${TRANSLATION_PATH}.priorAuthorizationCase`)}</span></TextColor></Semibold></Body>
              <Shelf alignment="center" spacing="small">
                <Subtitle testId="prior-authorization">{drugNameWithDetails}</Subtitle>
                {priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response) && caseOutcome == "in_progress" ? (
                  <Tag type="warning-high-contrast">{translate("PRIOR_AUTHORIZATION.awaitingResponse")}</Tag>
                ) : (
                  <Tag type={caseOutcomesObject[caseOutcome]?.[1] as TagType}>{caseOutcomesObject[caseOutcome]?.[0]}</Tag>
                )}
                <Shelf.Spacer />
              </Shelf>
            </Stack>

            {caseOutcome == "in_progress" && priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response) && (
              <Message appearance={Messages.Informational} visible>
                {translate(`${TRANSLATION_PATH}.additionalInformationAndDocumentation`)}
              </Message>
            )}

            {/* Case Summary Fields */}
            {renderCaseFields()}

            {/* Drug Information */}
            <Stack spacing="small">
              <SectionHeader title={translate(`${TRANSLATION_PATH}.prescriptionDetails`)} />
              <Stack spacing="none">
                <Heading>{drugNameWithDetails}</Heading>
                <InlineField label={translate(`${TRANSLATION_PATH}.quantity`)}>{priorAuthorizationCaseData?.drug_quantity}</InlineField>
                <InlineField label={translate(`${TRANSLATION_PATH}.daysSupply`)}>{priorAuthorizationCaseData?.drug_days_supply}</InlineField>
                {priorAuthorizationCaseData?.drug_directions_for_use && (
                  <InlineField label={translate(`${TRANSLATION_PATH}.directionsForUse`)}>{priorAuthorizationCaseData?.drug_directions_for_use}</InlineField>
                )}
              </Stack>
            </Stack>

            {/* Prescriber */}
            <Stack spacing="small">
              <SectionHeader title={translate(`${TRANSLATION_PATH}.prescriber`)} />
              <Stack spacing="none">
                <Heading style={{ textTransform: "uppercase" }}>{prescriberDisplayName}</Heading>
                {priorAuthorizationCaseData?.prescriber_npi && (
                  <InlineField label={translate(`${TRANSLATION_PATH}.npi`)}>{priorAuthorizationCaseData?.prescriber_npi}</InlineField>
                )}
                {formattedPrescriberPhoneNumber && (
                  <InlineField label={translate(`${TRANSLATION_PATH}.phone`)}><a href={`tel:${formattedPrescriberPhoneNumber}`}>{formattedPrescriberPhoneNumber}</a></InlineField>
                )}
              </Stack>
            </Stack>

            {/* Documents */}
            {!isLoading && !isError && documentListData?.results
              && +documentListData?.result_count > 0 && (
              <Stack spacing="small">
                <SectionHeader title={translate(`${TRANSLATION_PATH}.documents`)} />
                <Stack spacing="small" alignment="left">
                  {documentListData.results.map((pa_document) => (
                    <Button key={pa_document.letter.uuid} appearance={Buttons.Functional.Tertiary.Compact} icon="link-out" iconPosition="right">
                      {pa_document.letter.filename}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            )}

            {/* Customer Support */}
            <Stack spacing="small">
              <SectionHeader title={translate(`${TRANSLATION_PATH}.customerSupport`)} />
              <Body>
                {translate(`${TRANSLATION_PATH}.contactCustomerCareAt`)}{" "}
                <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
              </Body>
            </Stack>
          </Stack>
        </LoadingGroup.Placeholder>
      </PrintableWrapper>
    </LoadingGroup>
  );
};

const PADetailsSkeleton = () => {
  return (
    <Stack spacing="large">
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <Stack spacing="mini">
        <LoadingSkeleton style={{ height: 15, width: 125 }} />
        <LoadingSkeleton style={{ height: 20, width: 250 }} />
        <LoadingSkeleton style={{ height: 20, width: 250 }} />
        <LoadingSkeleton style={{ height: 20, width: 250 }} />
      </Stack>
      <Stack spacing="mini">
        <LoadingSkeleton style={{ height: 15, width: 125 }} />
        <LoadingSkeleton style={{ height: 20, width: 250 }} />
        <LoadingSkeleton style={{ height: 20, width: 250 }} />
        <LoadingSkeleton style={{ height: 20, width: 250 }} />
      </Stack>
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
    </Stack>
  );
};
